import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  FilterIcon,
  X,
  Plus,
  Search,
  CalendarIcon,
  Loader2,
} from 'lucide-react';
import AppointmentList from '@/components/ui/appointment-list';
import {
  getUserBookings,
  getEventTypes,
  getOrganisationMembers,
  listOrganisations,
  getPatients,
  createDoctorBooking,
  getTimeSlots,
  getCurrentUser,
} from '@/client/sdk.gen';
import {
  BookingResponse,
  BookingFilterRequest,
  EventTypeResponse,
  OrganisationMemberResponse,
  OrganisationResponse,
  PatientRead,
  TimeSlotItem,
  UserResponse,
} from '@/client/types.gen';
import { Skeleton } from '@/components/ui/skeleton';
import DashboardLayout from '@/app/dashboard/layout';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { format, addDays } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Textarea } from '@/components/ui/textarea';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';

// Define ExtendedBookingResponse to match the one from BookingList
interface ExtendedBookingResponse extends BookingResponse {
  uid: string;
}

// Define EventTypeWithHosts to provide type safety
interface EventTypeWithHosts extends EventTypeResponse {
  user_id: string;
  event_type_hosts?: OrganisationMemberResponse[];
  is_active?: boolean;
}

// Define OrganisationMemberWithUsername to provide type safety
interface OrganisationMemberWithUsername extends OrganisationMemberResponse {
  username?: string;
}

const filterFormSchema = z.object({
  event_type_id: z.union([z.literal('all_event_types'), z.string()]).optional(),
  team_id: z.union([z.literal('all_organisations'), z.string()]).optional(),
  member_id: z.union([z.literal('all_team_members'), z.string()]).optional(),
  attendee_names: z.array(z.string()).optional(),
  attendee_emails: z.array(z.string()).optional(),
  start_date: z.date().optional(),
  end_date: z.date().optional(),
});

type FilterFormValues = z.infer<typeof filterFormSchema>;

// Define CreateAppointmentFormSchema
const createAppointmentFormSchema = z.object({
  patient_id: z.string({
    required_error: 'Please select a patient',
  }),
  event_type_id: z.string({
    required_error: 'Please select an appointment type',
  }),
  start_time: z.string({
    required_error: 'Please select a start time',
  }),
  description: z.string().optional(),
});

type CreateAppointmentFormValues = z.infer<typeof createAppointmentFormSchema>;

export default function BookingsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [bookings, setBookings] = useState<ExtendedBookingResponse[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterFormValues>({});
  const [filterCount, setFilterCount] = useState(0);

  // Add states for dropdown options
  const [eventTypes, setEventTypes] = useState<EventTypeResponse[]>([]);
  const [isLoadingEventTypes, setIsLoadingEventTypes] = useState(false);

  const [organisations, setOrganisations] = useState<OrganisationResponse[]>(
    []
  );
  const [isLoadingOrganisations, setIsLoadingOrganisations] = useState(false);

  const [orgMembers, setOrgMembers] = useState<OrganisationMemberResponse[]>(
    []
  );
  const [isLoadingOrgMembers, setIsLoadingOrgMembers] = useState(false);

  // Add new states for create appointment dialog
  const [createAppointmentDialogOpen, setCreateAppointmentDialogOpen] =
    useState(false);
  const [patients, setPatients] = useState<PatientRead[]>([]);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const [selectedUsername, setSelectedUsername] = useState<string>('');
  const [selectedEventTypeSlug, setSelectedEventTypeSlug] =
    useState<string>('');
  const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlotItem[]>(
    []
  );
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [currentUser, setCurrentUser] = useState<UserResponse | null>(null);
  const { toast } = useToast();

  // Set up form with react-hook-form
  const form = useForm<FilterFormValues>({
    resolver: zodResolver(filterFormSchema),
    defaultValues: {
      event_type_id: 'all_event_types',
      team_id: 'all_organisations',
      member_id: 'all_team_members',
    },
  });

  // Set up form with react-hook-form for create appointment
  const createAppointmentForm = useForm<CreateAppointmentFormValues>({
    resolver: zodResolver(createAppointmentFormSchema),
    defaultValues: {
      description: '',
    },
  });

  // Function to transform BookingResponse to ExtendedBookingResponse
  const transformBookings = (
    bookings: BookingResponse[]
  ): ExtendedBookingResponse[] => {
    return bookings.map((booking) => ({
      ...booking,
      uid: booking.id, // Just use booking.id as the uid since BookingResponse doesn't have a uid property
    }));
  };

  // Function to refresh bookings
  const refreshBookings = useCallback(async () => {
    try {
      const filterRequest: BookingFilterRequest = {
        page,
        per_page: 10,
      };

      // Add user selected filters
      if (
        activeFilters.event_type_id &&
        activeFilters.event_type_id !== 'all_event_types'
      ) {
        filterRequest.event_type_id = activeFilters.event_type_id;
      }

      if (
        activeFilters.team_id &&
        activeFilters.team_id !== 'all_organisations'
      ) {
        filterRequest.team_id = activeFilters.team_id;
      }

      if (
        activeFilters.member_id &&
        activeFilters.member_id !== 'all_team_members'
      ) {
        filterRequest.member_id = activeFilters.member_id;
      }

      if (activeFilters.attendee_names?.length) {
        filterRequest.attendee_names = activeFilters.attendee_names;
      }

      if (activeFilters.attendee_emails?.length) {
        filterRequest.attendee_emails = activeFilters.attendee_emails;
      }

      // Add date filters - convert to ISO string for API
      if (activeFilters.start_date) {
        filterRequest.start_date = activeFilters.start_date.toISOString();
      }

      if (activeFilters.end_date) {
        filterRequest.end_date = activeFilters.end_date.toISOString();
      }

      const response = await getUserBookings({
        body: filterRequest,
      });

      if (response.data) {
        // Transform the bookings to include uid
        setBookings(transformBookings(response.data.bookings));
        setTotalPages(response.data.total_pages);
      }
    } catch (error) {
      console.error('Failed to refresh appointments:', error);
      throw error; // Rethrow to be caught by the BookingList component
    }
  }, [page, activeFilters]);

  // Fetch dropdown options when filter dialog opens
  useEffect(() => {
    if (filterDialogOpen) {
      fetchEventTypes();
      fetchOrganisations();
      fetchOrganisationMembers();
    }
  }, [filterDialogOpen]);

  // Fetch event types and org members when create appointment dialog opens
  useEffect(() => {
    if (createAppointmentDialogOpen) {
      fetchEventTypes();
      fetchOrganisationMembers();
      fetchPatients();
    }
  }, [createAppointmentDialogOpen]);

  // Fetch event types for dropdown
  const fetchEventTypes = async () => {
    setIsLoadingEventTypes(true);
    try {
      const response = await getEventTypes({
        query: {
          include_inactive: true,
        },
      });
      if (response.data) {
        setEventTypes(response.data.event_types);
      }
    } catch (error) {
      console.error('Failed to fetch appointment types:', error);
    } finally {
      setIsLoadingEventTypes(false);
    }
  };

  // Fetch organisations
  const fetchOrganisations = async () => {
    setIsLoadingOrganisations(true);
    try {
      const response = await listOrganisations();
      if (response.data) {
        setOrganisations(response.data.organisations);
      }
    } catch (error) {
      console.error('Failed to fetch organisations:', error);
    } finally {
      setIsLoadingOrganisations(false);
    }
  };

  // Fetch organisation members
  const fetchOrganisationMembers = async () => {
    setIsLoadingOrgMembers(true);
    try {
      const response = await getOrganisationMembers();
      if (response.data) {
        setOrgMembers(response.data.members);
      }
    } catch (error) {
      console.error('Failed to fetch organisation members:', error);
    } finally {
      setIsLoadingOrgMembers(false);
    }
  };

  // Fetch bookings when page or filters change
  useEffect(() => {
    setIsLoading(true);
    refreshBookings()
      .catch((error) => console.error('Error in initial fetch:', error))
      .finally(() => setIsLoading(false));
  }, [page, activeFilters, refreshBookings]);

  // Handle filter submission
  const onSubmitFilters = (values: FilterFormValues) => {
    setActiveFilters(values);
    setFilterDialogOpen(false);
    setPage(1); // Reset to first page when filters change

    // Count active filters for badge - exclude "all_*" values
    const filterFields = Object.entries(values).filter(([key, value]) => {
      if (value === undefined) return false;
      if (Array.isArray(value)) return value.length > 0;

      // Exclude "all_*" values
      if (key === 'event_type_id' && value === 'all_event_types') return false;
      if (key === 'team_id' && value === 'all_organisations') return false;
      if (key === 'member_id' && value === 'all_team_members') return false;

      return true;
    });
    setFilterCount(filterFields.length);
  };

  // Clear all filters
  const clearFilters = () => {
    form.reset({
      event_type_id: 'all_event_types',
      team_id: 'all_organisations',
      member_id: 'all_team_members',
    });
    setActiveFilters({
      event_type_id: 'all_event_types',
      team_id: 'all_organisations',
      member_id: 'all_team_members',
    });
    setFilterCount(0);
    setFilterDialogOpen(false);
  };

  // Handle individual email or name input
  const [attendeeNameInput, setAttendeeNameInput] = useState('');
  const [attendeeEmailInput, setAttendeeEmailInput] = useState('');

  const addAttendeeName = () => {
    if (!attendeeNameInput.trim()) return;

    const currentNames = form.getValues('attendee_names') || [];
    form.setValue('attendee_names', [
      ...currentNames,
      attendeeNameInput.trim(),
    ]);
    setAttendeeNameInput('');
  };

  const addAttendeeEmail = () => {
    if (!attendeeEmailInput.trim()) return;

    const currentEmails = form.getValues('attendee_emails') || [];
    form.setValue('attendee_emails', [
      ...currentEmails,
      attendeeEmailInput.trim(),
    ]);
    setAttendeeEmailInput('');
  };

  const removeAttendeeName = (index: number) => {
    const currentNames = form.getValues('attendee_names') || [];
    form.setValue(
      'attendee_names',
      currentNames.filter((_, i) => i !== index)
    );
  };

  const removeAttendeeEmail = (index: number) => {
    const currentEmails = form.getValues('attendee_emails') || [];
    form.setValue(
      'attendee_emails',
      currentEmails.filter((_, i) => i !== index)
    );
  };

  // Fetch patients when the create appointment dialog opens
  useEffect(() => {
    if (createAppointmentDialogOpen) {
      fetchPatients();
    }
  }, [createAppointmentDialogOpen]);

  // Fetch patients for dropdown
  const fetchPatients = async () => {
    setIsLoadingPatients(true);
    try {
      const response = await getPatients();
      if (response.data) {
        setPatients(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      toast({
        title: 'Error',
        description: 'Failed to load patients. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingPatients(false);
    }
  };

  // Fetch current user data when component mounts
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await getCurrentUser();
        console.log('Current user data:', response);
        if (response.data) {
          setCurrentUser(response.data);

          // If we have a username, set it immediately to enable time slot fetching
          if (response.data.username) {
            setSelectedUsername(response.data.username);
            console.log(
              'Setting current user username:',
              response.data.username
            );
          }
        }
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      }
    };

    fetchCurrentUser();
  }, []);

  // When event type changes, get the username and slug for time slots
  const handleEventTypeChange = (eventTypeId: string) => {
    console.log('Selected event type ID:', eventTypeId);
    createAppointmentForm.setValue('event_type_id', eventTypeId);

    // Find the event type details to get slug
    const eventType = eventTypes.find((et) => et.id === eventTypeId) as
      | EventTypeWithHosts
      | undefined;
    console.log('Found event type:', eventType);

    if (eventType) {
      setSelectedEventType(eventTypeId);
      setSelectedEventTypeSlug(eventType.slug);

      // For debugging
      console.log('Event type details:', {
        id: eventType.id,
        name: eventType.name,
        slug: eventType.slug,
        userId: eventType.user_id,
        hosts: eventType.event_type_hosts,
      });

      // Use the current user's username from the API response
      if (currentUser && currentUser.username) {
        setSelectedUsername(currentUser.username);
        console.log("Using current user's username:", currentUser.username);
      } else {
        // Fallbacks if currentUser username is not available
        // Try to find the username - first check if event has hosts
        if (
          eventType.event_type_hosts &&
          eventType.event_type_hosts.length > 0
        ) {
          // Use the first host's username or email as fallback
          const firstHost = eventType
            .event_type_hosts[0] as OrganisationMemberWithUsername;
          if (firstHost.username) {
            setSelectedUsername(firstHost.username);
            console.log('Using host username:', firstHost.username);
          } else {
            // If no username, use email as username (many systems allow this)
            setSelectedUsername(firstHost.email);
            console.log('Using host email as username:', firstHost.email);
          }
        } else {
          // If no hosts, try to find the creator by user_id
          const owner = orgMembers.find(
            (member) => member.id === eventType.user_id
          ) as OrganisationMemberWithUsername | undefined;
          console.log('Found owner:', owner);

          if (owner) {
            if (owner.username) {
              setSelectedUsername(owner.username);
              console.log('Using owner username:', owner.username);
            } else {
              // If no username, use email as username
              setSelectedUsername(owner.email);
              console.log('Using owner email as username:', owner.email);
            }
          } else {
            // Last resort - use current user's email if we can't find anything else
            console.log('No username found, using first org member');
            if (orgMembers.length > 0) {
              setSelectedUsername(orgMembers[0].email);
              console.log('Using first org member email:', orgMembers[0].email);
            }
          }
        }
      }

      // Reset the time slots when event type changes
      setAvailableTimeSlots([]);
      createAppointmentForm.setValue('start_time', '');

      // Force fetch time slots after a short delay to ensure username and slug are set
      setTimeout(() => {
        if (selectedDate) {
          fetchTimeSlots();
        }
      }, 100);
    }
  };

  // Fetch time slots when date or event type changes
  useEffect(() => {
    if (selectedEventTypeSlug && selectedUsername && selectedDate) {
      console.log('Dependencies changed, fetching time slots with:', {
        slug: selectedEventTypeSlug,
        username: selectedUsername,
        date: selectedDate,
      });
      fetchTimeSlots();
    }
  }, [selectedDate, selectedEventTypeSlug, selectedUsername]);

  const fetchTimeSlots = async () => {
    if (!selectedEventTypeSlug || !selectedUsername) {
      console.error('Cannot fetch time slots - missing slug or username', {
        slug: selectedEventTypeSlug,
        username: selectedUsername,
      });
      return;
    }

    console.log('Fetching time slots with:', {
      username: selectedUsername,
      eventTypeSlug: selectedEventTypeSlug,
      year: selectedDate.getFullYear(),
      month: selectedDate.getMonth() + 1,
      day: selectedDate.getDate(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    setIsLoadingTimeSlots(true);
    try {
      const response = await getTimeSlots({
        path: {
          username: selectedUsername,
          event_type_slug: selectedEventTypeSlug,
        },
        query: {
          year: selectedDate.getFullYear(),
          month: selectedDate.getMonth() + 1, // JavaScript months are 0-indexed
          day: selectedDate.getDate(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });

      console.log('Time slots API response:', response);

      if (response.data) {
        setAvailableTimeSlots(response.data.time_slots || []);
        console.log('Available time slots:', response.data.time_slots);
      } else if (response.error) {
        console.error('Error in time slots response:', response.error);
        toast({
          title: 'Error',
          description:
            'Failed to load available times. Check console for details.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to fetch time slots:', error);
      toast({
        title: 'Error',
        description:
          'Failed to load available times. Check console for details.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  // Create appointment submit handler
  const onCreateAppointment = async (values: CreateAppointmentFormValues) => {
    try {
      const response = await createDoctorBooking({
        body: {
          patient_id: values.patient_id,
          event_type_id: values.event_type_id,
          start_time: values.start_time,
          description: values.description || null,
        },
      });

      if (response.data) {
        toast({
          title: 'Success',
          description: 'Appointment created successfully.',
        });
        setCreateAppointmentDialogOpen(false);
        refreshBookings(); // Refresh the list to show the new booking
        createAppointmentForm.reset(); // Reset form
      }
    } catch (error) {
      console.error('Failed to create appointment:', error);
      toast({
        title: 'Error',
        description: 'Failed to create appointment. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const filteredPatients = patients.filter((patient) => {
    if (!patientSearchQuery) return true;
    const searchLower = patientSearchQuery.toLowerCase();

    return (
      patient.name.toLowerCase().includes(searchLower) ||
      (patient.email && patient.email.toLowerCase().includes(searchLower)) ||
      patient.phone.includes(patientSearchQuery)
    );
  });

  return (
    <DashboardLayout
      breadcrumbs={[
        { label: 'Home', href: '/dashboard' },
        { label: 'Appointments' },
      ]}
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Appointments</h1>
            <p className="text-muted-foreground">
              See upcoming and past appointments booked through your appointment
              type links.
            </p>
          </div>

          {/* Add Create Appointment Button */}
          <Button
            onClick={() => setCreateAppointmentDialogOpen(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" /> Create Appointment
          </Button>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div></div>

            <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 gap-1">
                  <FilterIcon className="h-4 w-4" />
                  Filter
                  {filterCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-1 rounded-full h-5 w-5 p-0 flex items-center justify-center"
                    >
                      {filterCount}
                    </Badge>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[525px]">
                <DialogHeader>
                  <DialogTitle>Filter Appointments</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmitFilters)}
                    className="space-y-6 py-4"
                  >
                    {(isLoadingEventTypes ||
                      isLoadingOrganisations ||
                      isLoadingOrgMembers) && (
                      <div className="space-y-2">
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-8 w-full" />
                      </div>
                    )}

                    <div
                      className={`grid grid-cols-1 gap-4 ${
                        isLoadingEventTypes ||
                        isLoadingOrganisations ||
                        isLoadingOrgMembers
                          ? 'opacity-50 pointer-events-none'
                          : ''
                      }`}
                    >
                      <FormField
                        control={form.control}
                        name="event_type_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Appointment Type</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                field.onChange(
                                  value === 'all_event_types'
                                    ? undefined
                                    : value
                                );
                              }}
                              value={field.value || 'all_event_types'}
                              disabled={isLoadingEventTypes}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select an appointment type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {isLoadingEventTypes ? (
                                  <SelectItem value="loading" disabled>
                                    <Skeleton className="h-4 w-24" />
                                  </SelectItem>
                                ) : (
                                  <>
                                    <SelectItem value="all_event_types">
                                      All appointment types
                                    </SelectItem>
                                    {eventTypes.map(
                                      (eventType: EventTypeWithHosts) => (
                                        <SelectItem
                                          key={eventType.id}
                                          value={eventType.id}
                                        >
                                          {eventType.name}
                                        </SelectItem>
                                      )
                                    )}
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="team_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Organisation</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                field.onChange(
                                  value === 'all_organisations'
                                    ? undefined
                                    : value
                                );
                              }}
                              value={field.value || 'all_organisations'}
                              disabled={isLoadingOrganisations}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select an organisation" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {isLoadingOrganisations ? (
                                  <SelectItem value="loading" disabled>
                                    <Skeleton className="h-4 w-24" />
                                  </SelectItem>
                                ) : (
                                  <>
                                    <SelectItem value="all_organisations">
                                      All organisations
                                    </SelectItem>
                                    {organisations.map((org) => (
                                      <SelectItem key={org.id} value={org.id}>
                                        {org.name}
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="member_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Team Member</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                field.onChange(
                                  value === 'all_team_members'
                                    ? undefined
                                    : value
                                );
                              }}
                              value={field.value || 'all_team_members'}
                              disabled={isLoadingOrgMembers}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a team member" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {isLoadingOrgMembers ? (
                                  <SelectItem value="loading" disabled>
                                    <Skeleton className="h-4 w-24" />
                                  </SelectItem>
                                ) : (
                                  <>
                                    <SelectItem value="all_team_members">
                                      All team members
                                    </SelectItem>
                                    {orgMembers.map((member) => (
                                      <SelectItem
                                        key={member.id}
                                        value={member.id}
                                      >
                                        {member.email}
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="attendee_names"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Patient Names</FormLabel>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Add patient name"
                                value={attendeeNameInput}
                                onChange={(e) =>
                                  setAttendeeNameInput(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addAttendeeName();
                                  }
                                }}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={addAttendeeName}
                              >
                                Add
                              </Button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {(field.value || []).map((name, index) => (
                                <Badge
                                  key={index}
                                  variant="secondary"
                                  className="flex gap-1 items-center"
                                >
                                  {name}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="p-0 h-4 w-4"
                                    onClick={() => removeAttendeeName(index)}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </Badge>
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="attendee_emails"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Patient Emails</FormLabel>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Add patient email"
                                value={attendeeEmailInput}
                                onChange={(e) =>
                                  setAttendeeEmailInput(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addAttendeeEmail();
                                  }
                                }}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={addAttendeeEmail}
                              >
                                Add
                              </Button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {(field.value || []).map((email, index) => (
                                <Badge
                                  key={index}
                                  variant="secondary"
                                  className="flex gap-1 items-center"
                                >
                                  {email}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="p-0 h-4 w-4"
                                    onClick={() => removeAttendeeEmail(index)}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </Badge>
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="start_date"
                          render={({ field }) => (
                            <FormItem className="flex flex-col">
                              <FormLabel>Start Date</FormLabel>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      variant="outline"
                                      className={cn(
                                        'pl-3 text-left font-normal',
                                        !field.value && 'text-muted-foreground'
                                      )}
                                    >
                                      {field.value ? (
                                        format(field.value, 'PPP')
                                      ) : (
                                        <span>Pick a date</span>
                                      )}
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-auto p-0"
                                  align="start"
                                >
                                  <CalendarComponent
                                    mode="single"
                                    selected={field.value || undefined}
                                    onSelect={field.onChange}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="end_date"
                          render={({ field }) => (
                            <FormItem className="flex flex-col">
                              <FormLabel>End Date</FormLabel>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      variant="outline"
                                      className={cn(
                                        'pl-3 text-left font-normal',
                                        !field.value && 'text-muted-foreground'
                                      )}
                                    >
                                      {field.value ? (
                                        format(field.value, 'PPP')
                                      ) : (
                                        <span>Pick a date</span>
                                      )}
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-auto p-0"
                                  align="start"
                                >
                                  <CalendarComponent
                                    mode="single"
                                    selected={field.value || undefined}
                                    onSelect={field.onChange}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <DialogFooter className="flex justify-between sm:justify-between">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={clearFilters}
                      >
                        Clear Filters
                      </Button>
                      <Button
                        type="submit"
                        disabled={
                          isLoadingEventTypes ||
                          isLoadingOrganisations ||
                          isLoadingOrgMembers
                        }
                      >
                        Apply Filters
                      </Button>
                    </DialogFooter>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>

          <AppointmentList
            bookings={bookings}
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            isLoading={isLoading}
            refreshList={refreshBookings}
          />
        </div>

        {/* Create Appointment Dialog */}
        <Dialog
          open={createAppointmentDialogOpen}
          onOpenChange={setCreateAppointmentDialogOpen}
        >
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Create New Appointment</DialogTitle>
              <DialogDescription>
                Schedule an appointment for a patient without requiring their
                interaction.
              </DialogDescription>
            </DialogHeader>
            <Form {...createAppointmentForm}>
              <form
                onSubmit={createAppointmentForm.handleSubmit(
                  onCreateAppointment
                )}
                className="space-y-6 py-4"
              >
                {/* Patient Selection */}
                <FormField
                  control={createAppointmentForm.control}
                  name="patient_id"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Patient</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              className={cn(
                                'w-full justify-between',
                                !field.value && 'text-muted-foreground'
                              )}
                            >
                              {field.value
                                ? patients.find(
                                    (patient) => patient.id === field.value
                                  )?.name
                                : 'Select patient'}
                              <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput
                              placeholder="Search patients..."
                              onValueChange={setPatientSearchQuery}
                            />
                            <CommandEmpty>No patients found.</CommandEmpty>
                            <CommandGroup className="max-h-64 overflow-auto">
                              {isLoadingPatients ? (
                                <div className="p-2">
                                  <Skeleton className="h-8 w-full" />
                                  <Skeleton className="h-8 w-full mt-2" />
                                  <Skeleton className="h-8 w-full mt-2" />
                                </div>
                              ) : (
                                filteredPatients.map((patient) => (
                                  <CommandItem
                                    key={patient.id}
                                    onSelect={() => {
                                      createAppointmentForm.setValue(
                                        'patient_id',
                                        patient.id
                                      );
                                    }}
                                    className="flex flex-col items-start"
                                  >
                                    <div className="font-medium">
                                      {patient.name}
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                      {patient.email || patient.phone}
                                    </div>
                                  </CommandItem>
                                ))
                              )}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Appointment Type */}
                <FormField
                  control={createAppointmentForm.control}
                  name="event_type_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Appointment Type</FormLabel>
                      <Select
                        onValueChange={handleEventTypeChange}
                        value={field.value}
                        disabled={isLoadingEventTypes}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select an appointment type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {isLoadingEventTypes ? (
                            <SelectItem value="loading" disabled>
                              <Skeleton className="h-4 w-24" />
                            </SelectItem>
                          ) : (
                            eventTypes.map((eventType: EventTypeWithHosts) => (
                              <SelectItem
                                key={eventType.id}
                                value={eventType.id}
                                className="flex flex-col items-start"
                              >
                                <div className="flex items-center gap-2">
                                  <span>{eventType.name}</span>
                                  {!eventType.is_active && (
                                    <Badge variant="outline" className="ml-2">
                                      Inactive
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {eventType.duration} min
                                </span>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Date Selection */}
                <FormItem className="flex flex-col">
                  <FormLabel>Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !selectedDate && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate
                          ? format(selectedDate, 'PPP')
                          : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        disabled={(date) => date < addDays(new Date(), -1)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </FormItem>

                {/* Time Selection */}
                <FormField
                  control={createAppointmentForm.control}
                  name="start_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Time</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={isLoadingTimeSlots || !selectedEventType}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                selectedEventType
                                  ? 'Select a time'
                                  : 'Select an appointment type first'
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {isLoadingTimeSlots ? (
                            <SelectItem value="loading" disabled>
                              <Skeleton className="h-4 w-24" />
                            </SelectItem>
                          ) : availableTimeSlots.length > 0 ? (
                            availableTimeSlots.map((slot) => (
                              <SelectItem
                                key={slot.start_time}
                                value={slot.start_time}
                              >
                                {format(new Date(slot.start_time), 'h:mm a')} -{' '}
                                {format(new Date(slot.end_time), 'h:mm a')}
                                {slot.slots_left !== undefined &&
                                  slot.slots_left !== null && (
                                    <span className="ml-2 text-xs text-muted-foreground">
                                      ({slot.slots_left} slot
                                      {slot.slots_left !== 1 ? 's' : ''} left)
                                    </span>
                                  )}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="no-slots" disabled>
                              No available times for this date
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Description */}
                <FormField
                  control={createAppointmentForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description (Optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Add notes about this appointment"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setCreateAppointmentDialogOpen(false);
                      createAppointmentForm.reset();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createAppointmentForm.formState.isSubmitting}
                  >
                    {createAppointmentForm.formState.isSubmitting && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Create Appointment
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
